"use client";

import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Variant = "primary" | "ghost";

/**
 * Tactile button. Compresses on press (scale-down + shadow tightens to inset)
 * and lifts on hover, using spring physics so it reads as a raised, pressable
 * object rather than a flat rectangle.
 *
 * Sizing: 16px text and a 48px minimum height — comfortably above the 44px
 * touch-target floor. Primary is a solid accent fill with theme-aware ink;
 * ghost is deliberately subordinate (glass, no fill) so it never competes
 * with the one dominant CTA on screen.
 */
export function Button({
  children,
  variant = "primary",
  href,
  onClick,
  className,
}: {
  children: ReactNode;
  variant?: Variant;
  href?: string;
  onClick?: () => void;
  className?: string;
}) {
  const base =
    "neo inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl2 px-6 py-3 text-base font-semibold tracking-tight select-none";
  const styles =
    variant === "primary"
      ? "text-accent-ink bg-accent"
      : "glass border border-border text-text";

  const Comp = href ? motion.a : motion.button;

  return (
    <Comp
      href={href}
      type={href ? undefined : "button"}
      onClick={onClick}
      className={cn(base, styles, className)}
      whileHover={{ scale: 1.03, y: -1 }}
      whileTap={{ scale: 0.96, y: 1 }}
      transition={{ type: "spring", stiffness: 420, damping: 20 }}
      style={
        variant === "primary"
          ? { boxShadow: "0 0 0 1px color-mix(in srgb, var(--accent) 60%, transparent), 0 4px 14px rgba(0, 0, 0, 0.35)" }
          : undefined
      }
    >
      {children}
    </Comp>
  );
}
